import React, { useState, useEffect } from 'react';
import { TrendingUp, Copy, Check } from 'lucide-react';

type CompoundFrequency = 'annually' | 'semiannually' | 'quarterly' | 'monthly' | 'daily';

const CompoundInterestCalculator: React.FC = () => {
  const [principal, setPrincipal] = useState('10000');
  const [rate, setRate] = useState('7');
  const [time, setTime] = useState('10');
  const [frequency, setFrequency] = useState<CompoundFrequency>('annually');
  const [monthlyContribution, setMonthlyContribution] = useState('100');
  const [futureValue, setFutureValue] = useState('');
  const [totalContributions, setTotalContributions] = useState('');
  const [totalInterest, setTotalInterest] = useState('');
  const [copied, setCopied] = useState(false);

  const frequencyMap: Record<CompoundFrequency, { n: number; label: string }> = {
    annually: { n: 1, label: 'Annually' },
    semiannually: { n: 2, label: 'Semi-annually' },
    quarterly: { n: 4, label: 'Quarterly' },
    monthly: { n: 12, label: 'Monthly' },
    daily: { n: 365, label: 'Daily' },
  };

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principal, rate, time, frequency, monthlyContribution]);

  const calculate = () => {
    const P = parseFloat(principal);
    const r = parseFloat(rate) / 100;
    const t = parseFloat(time);
    const n = frequencyMap[frequency].n;
    const PMT = parseFloat(monthlyContribution);

    if (isNaN(P) || isNaN(r) || isNaN(t) || isNaN(PMT) || P < 0 || t <= 0) {
      setFutureValue(''); setTotalContributions(''); setTotalInterest('');
      return;
    }

    // Compound interest: A = P(1 + r/n)^(nt)
    const compoundPrincipal = P * Math.pow(1 + r / n, n * t);
    
    // Future value of series (monthly contributions)
    const monthlyRate = r / 12;
    const totalMonths = t * 12;
    let contributionFV = 0;
    if (PMT > 0 && monthlyRate > 0) {
      contributionFV = PMT * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
    } else if (PMT > 0) {
      contributionFV = PMT * totalMonths;
    }

    const total = compoundPrincipal + contributionFV;
    const contributions = P + (PMT * totalMonths);
    const interest = total - contributions;

    setFutureValue(total.toFixed(2));
    setTotalContributions(contributions.toFixed(2));
    setTotalInterest(interest.toFixed(2));
  };

  const copyResult = async () => {
    if (!futureValue) return;
    await navigator.clipboard.writeText(`$${futureValue}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (value: string) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Compound Interest Calculator</h2>
        <p className="text-gray-600">See how your investments grow over time</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Investment ($)</label>
              <input
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Contribution ($)</label>
              <input
                type="number"
                value={monthlyContribution}
                onChange={(e) => setMonthlyContribution(e.target.value)}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="100"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Annual Interest Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="7"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
              <input
                type="number"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compound Frequency</label>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(frequencyMap).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => setFrequency(key as CompoundFrequency)}
                  className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                    frequency === key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Future Value</p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-2xl font-bold text-indigo-600">{formatCurrency(futureValue)}</p>
                <button onClick={copyResult} className="p-1 text-gray-400 hover:text-gray-600">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Contributions</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalContributions)}</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Interest Earned</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalInterest)}</p>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-indigo-900 mb-1">The Power of Compound Interest</h4>
              <p className="text-sm text-indigo-700">
                A = P(1 + r/n)^(nt) • More frequent compounding = more growth • 
                Starting early makes a huge difference due to exponential growth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompoundInterestCalculator;
